import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ShippableOrder } from "../types";
import { turkishIncludes } from "../utils/formatters";
import { firmHue } from "./FirmPicker";
import { fmtDate, fmtQty } from "../pages/Shipping";
import "./FirmPicker.css";
import "./OrderPicker.css";

/**
 * Sipariş seçici — sevkiyata girecek siparişler buradan işaretlenir (firma seçicinin kardeşi).
 * Modal kendisi VERİ ÇEKMEZ: form'un yüklediği `orders` dizisiyle çalışır, arama/sıralama istemci tarafındadır.
 * Çoklu seçim taslakta birikir; forma yalnız "Uygula" (Ctrl+Enter) ile iner, Esc/Vazgeç/overlay taslağı atar.
 */

export interface OrderPickerModalProps {
  open: boolean;
  /** Vazgeç / Esc / X / overlay: taslak atılır, onApply çağrılmaz. */
  onClose: () => void;
  /** Listelenecek siparişler (düzenleme kipinde `remaining` bu sevkiyatın kendi miktarıyla birleştirilmiş gelir). */
  orders: ShippableOrder[];
  loading?: boolean;
  /** Doluysa listenin yerine hata bloğu + "Yeniden dene" gösterilir. */
  error?: string;
  onReload?: () => void;
  /** Forma eklenmiş sipariş kimlikleri; taslak her açılışta bundan kopyalanır. */
  value: string[];
  /** "Uygula" / Ctrl+Enter — `orderIds` taslak sırasıdır (önce mevcutlar, sonra işaretleme sırası). */
  onApply: (orderIds: string[], picked: ShippableOrder[]) => void;
  /** Başlık altı: "BOSSA TEKSTİL · 12 sipariş · 4 seçili". */
  firmName?: string;
  title?: string;
}

type SortKey = "deadline" | "date" | "remaining";
type ProgressChip = "" | "none" | "partial";
type Row = { kind: "head"; key: string; label: string; icon?: React.ReactNode } | { kind: "order"; key: string; o: ShippableOrder };

const EPS = 0.0005;
let seq = 0;

const orderMark = (o: ShippableOrder) =>
  (o.modelCode || o.modelName || "SP").replace(/[^0-9A-Za-zÇĞİÖŞÜçğıöşü]/g, "").slice(0, 2).toUpperCase() || "SP";

/** Çok terimli Türkçe arama: her terim en az bir alanda geçmeli ("bsa 114" = "114 bsa"). */
const matches = (o: ShippableOrder, q: string) => {
  const terms = q.trim().split(/\s+/).filter(Boolean);
  if (!terms.length) return true;
  const hay = [o.modelCode, o.modelName, o.orderStatusName, o.invoiceNumber, fmtDate(o.acceptanceDate), fmtDate(o.deadline)]
    .filter(Boolean).map(String);
  return terms.every((t) => hay.some((s) => turkishIncludes(s, t)));
};

/** Termin durumu: geçti / 7 gün içinde / normal-yok. */
export const dueState = (iso?: string | null): "" | "soon" | "past" => {
  if (!iso) return "";
  const d = new Date(iso); d.setHours(0, 0, 0, 0);
  const t = new Date(); t.setHours(0, 0, 0, 0);
  const days = Math.round((d.getTime() - t.getTime()) / 86400000);
  return days < 0 ? "past" : days <= 7 ? "soon" : "";
};

const SORTS: { key: SortKey; label: string; dir: 1 | -1 }[] = [
  { key: "deadline", label: "Termin", dir: 1 },
  { key: "date", label: "Sipariş tarihi", dir: -1 },
  { key: "remaining", label: "Kalan", dir: -1 },
];

const I = {
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>,
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5L20 7" /></svg>,
  inbox: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z" /><path d="M4 13h5l2 3h2l2-3h5" /></svg>,
  box: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7.5 12 3l9 4.5v9L12 21l-9-4.5z" /><path d="M3 7.5 12 12l9-4.5M12 12v9" /></svg>,
  refresh: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12a8 8 0 1 1-2.3-5.6" /><path d="M20 4v5h-5" /></svg>,
};

export const OrderPickerModal: React.FC<OrderPickerModalProps> = ({ open, onClose, orders, loading, error, onReload, value, onApply, firmName, title = "Sipariş seç" }) => {
  const uid = useRef(`op${++seq}`).current;
  const [q, setQ] = useState("");
  const [progress, setProgress] = useState<ProgressChip>("");
  const [sort, setSort] = useState<SortKey>("deadline");
  const [dir, setDir] = useState<1 | -1>(1);
  const [showDone, setShowDone] = useState(false);
  const [cursorKey, setCursorKey] = useState<string | null>(null);
  const [draft, setDraft] = useState<Set<string>>(new Set());
  const [pinned, setPinned] = useState<string[]>([]);
  const [live, setLive] = useState("");
  const liveTimer = useRef<number | undefined>(undefined);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setQ(""); setProgress(""); setSort("deadline"); setDir(1); setShowDone(false); setCursorKey(null); setLive("");
    setDraft(new Set(value)); setPinned([...value]);
    const t = window.setTimeout(() => searchRef.current?.focus(), 30);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => () => window.clearTimeout(liveTimer.current), []);

  const doneCount = useMemo(() => orders.filter((o) => o.remaining <= EPS).length, [orders]);
  /** Listede karşılığı olan seçimler — sayaçlar ve Uygula bunları gösterir (liste dışı kalan kimlik sayılmaz). */
  const liveCount = useMemo(() => orders.filter((o) => draft.has(o.orderId)).length, [orders, draft]);
  const filtered = useMemo(() => {
    const base = orders.filter((o) => {
      if (!showDone && o.remaining <= EPS && !draft.has(o.orderId)) return false;
      if (progress === "none" && o.shipped > EPS) return false;
      if (progress === "partial" && !(o.shipped > EPS && o.remaining > EPS)) return false;
      return matches(o, q);
    });
    const cmp = (a: ShippableOrder, b: ShippableOrder) => {
      if (sort === "deadline") {
        if (!a.deadline && !b.deadline) return 0;
        if (!a.deadline) return 1;          // termini olmayan her zaman sonda
        if (!b.deadline) return -1;
        return (a.deadline < b.deadline ? -1 : a.deadline > b.deadline ? 1 : 0) * dir;
      }
      // Karşılaştırmalar artan sırada; yön çarpanla verilir (▲ artan, ▼ azalan)
      if (sort === "date") return (a.acceptanceDate < b.acceptanceDate ? -1 : a.acceptanceDate > b.acceptanceDate ? 1 : 0) * dir;
      return (a.remaining - b.remaining) * dir;
    };
    return [...base].sort(cmp);
  }, [orders, showDone, progress, q, sort, dir, draft]);

  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    if (!q.trim() && !progress && draft.size > 0) {
      // Sabitlenen grup süzülmemiş kaynaktan okunur: tamamı sevk edilmiş bir seçim de görünsün
      const inShipment = pinned.map((id) => orders.find((o) => o.orderId === id)).filter((o): o is ShippableOrder => !!o && draft.has(o.orderId));
      if (inShipment.length) {
        out.push({ kind: "head", key: "h-sel", label: `Sevkiyatta · ${liveCount}`, icon: I.check });
        inShipment.forEach((o) => out.push({ kind: "order", key: `s-${o.orderId}`, o }));
        out.push({ kind: "head", key: "h-all", label: `Tüm siparişler · ${filtered.length}`, icon: I.box });
      }
    }
    filtered.forEach((o) => out.push({ kind: "order", key: o.orderId, o }));
    return out;
  }, [filtered, orders, pinned, draft, liveCount, q, progress]);

  const orderRows = useMemo(() => rows.filter((r): r is Extract<Row, { kind: "order" }> => r.kind === "order"), [rows]);
  const keys = useMemo(() => orderRows.map((r) => r.key), [orderRows]);
  const cursor = useMemo(() => {
    if (!cursorKey) return 0;
    let i = keys.indexOf(cursorKey);
    if (i < 0 && cursorKey.startsWith("s-")) i = keys.indexOf(cursorKey.slice(2));
    return Math.max(0, i);
  }, [keys, cursorKey]);
  const moveCursor = (d: number) => setCursorKey(keys[Math.max(0, Math.min(keys.length - 1, cursor + d))] ?? null);
  useEffect(() => { setCursorKey(keys[0] ?? null); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, progress, showDone, sort, dir]);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-cursor="${cursor}"]`)?.scrollIntoView({ block: "nearest" });
  }, [cursor]);

  const announce = (msg: string) => {
    setLive(msg);
    window.clearTimeout(liveTimer.current);
    liveTimer.current = window.setTimeout(() => setLive(""), 1500);
  };
  const refocusSearch = () => { const el = searchRef.current; if (!el) return; el.focus(); if (el.value) el.select(); };

  const toggle = useCallback((o: ShippableOrder) => {
    const add = !draft.has(o.orderId);
    setDraft((d) => { const n = new Set(d); if (add) n.add(o.orderId); else n.delete(o.orderId); return n; });
    if (add) setPinned((p) => (p.includes(o.orderId) ? p : [...p, o.orderId]));
    announce(`${o.modelCode || "Sipariş"} ${add ? "eklendi" : "çıkarıldı"} · ${draft.size + (add ? 1 : -1)} seçili`);
    refocusSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  const allVisibleSel = filtered.length > 0 && filtered.every((o) => draft.has(o.orderId));
  const showBulk = !!(q.trim() || progress) && filtered.length > 0;
  const bulk = () => {
    const add = !allVisibleSel;
    const delta = filtered.filter((o) => draft.has(o.orderId) !== add).length;
    setDraft((d) => { const n = new Set(d); filtered.forEach((o) => { if (add) n.add(o.orderId); else n.delete(o.orderId); }); return n; });
    if (add) setPinned((p) => [...p, ...filtered.map((o) => o.orderId).filter((id) => !p.includes(id))]);
    announce(`${delta} sipariş ${add ? "eklendi" : "çıkarıldı"} · ${draft.size + (add ? delta : -delta)} seçili`);
    refocusSearch();
  };
  const clearAll = () => { setDraft(new Set()); announce("Seçim temizlendi"); refocusSearch(); };
  const resetFilters = () => { setQ(""); setProgress(""); setShowDone(false); refocusSearch(); };

  const commit = () => {
    const ids = [...pinned.filter((id) => draft.has(id)), ...Array.from(draft).filter((id) => !pinned.includes(id))];
    onApply(ids, ids.map((id) => orders.find((o) => o.orderId === id)).filter((o): o is ShippableOrder => !!o));
    onClose();
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onClose(); return; }
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); e.stopPropagation(); commit(); return; }
    if (e.target !== searchRef.current) return; // odaklı düğmede Enter kendi işini yapsın
    if (e.key === "ArrowDown") { e.preventDefault(); moveCursor(1); }
    else if (e.key === "ArrowUp") { e.preventDefault(); moveCursor(-1); }
    else if (e.key === "Enter") { e.preventDefault(); const row = orderRows[cursor]; if (row) toggle(row.o); }
  };

  const pickSort = (s: SortKey) => {
    if (s === sort) { setDir((d) => (d === 1 ? -1 : 1)); return; }
    setSort(s); setDir(SORTS.find((x) => x.key === s)!.dir);
  };

  if (!open) return null;
  const sub = loading ? "Siparişler yükleniyor…"
    : `${firmName ? firmName + " · " : ""}${orders.length} sipariş${doneCount ? ` · ${doneCount} tamamı sevk edilmiş` : ""} · ${liveCount} seçili`;
  let idx = 0;

  return createPortal(
    <div className="fp-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} onClick={(e) => e.stopPropagation()} onKeyDown={onKey}>
      <div className="fp-modal multi op-modal" role="dialog" aria-modal="true" aria-labelledby={`${uid}-t`} onMouseDown={(e) => e.stopPropagation()}>
        <header className="fp-head">
          <div>
            <span className="fp-kicker">Sipariş</span>
            <h2 id={`${uid}-t`}>{title}</h2>
            <p className="fp-sub">{sub}</p>
          </div>
          <button type="button" className="fp-ib big" aria-label="Kapat" onClick={onClose}>{I.close}</button>
        </header>

        <div className="fp-tools">
          <div className="fp-search">{I.search}
            <input ref={searchRef} placeholder="Model kodu, model adı, durum, fatura no veya tarih" value={q} onChange={(e) => setQ(e.target.value)} />
            {q && <button type="button" className="fp-ib" aria-label="Temizle" onClick={() => { setQ(""); searchRef.current?.focus(); }}>{I.close}</button>}
          </div>
          <div className="fp-chips">
            <button type="button" className={progress === "" ? "on" : ""} onClick={() => setProgress("")}>Tümü</button>
            <button type="button" className={progress === "none" ? "on" : ""} onClick={() => setProgress("none")}>Sevk edilmemiş</button>
            <button type="button" className={progress === "partial" ? "on" : ""} onClick={() => setProgress("partial")}>Kısmi sevk</button>
          </div>
          <div className="fp-chips op-sorts">
            {SORTS.map((s) => (
              <button key={s.key} type="button" className={sort === s.key ? "on" : ""} onClick={() => pickSort(s.key)} title={`${s.label} sırala`}>
                {s.label}{sort === s.key && <span className="op-dir">{dir === 1 ? "▲" : "▼"}</span>}
              </button>
            ))}
          </div>
          {doneCount > 0 && (
            <label className="fp-toggle"><input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} /> Tamamı sevk edilenleri göster</label>
          )}
          {showBulk && <button type="button" className="fp-btn" onClick={bulk}>{allVisibleSel ? `Görünenleri kaldır · ${filtered.length}` : `Görünenleri seç · ${filtered.length}`}</button>}
          {onReload && <button type="button" className="fp-btn" onClick={onReload} disabled={loading}>{I.refresh} Yenile</button>}
        </div>

        <div className="fp-list" ref={listRef} role="listbox" id={`${uid}-l`} aria-label="Siparişler" aria-multiselectable>
          {error && <div className="fp-blank"><b>{error}</b>{onReload && <button type="button" className="fp-btn" onClick={onReload}>Yeniden dene</button>}</div>}
          {!error && loading && rows.length === 0 && <div className="fp-blank"><b>Siparişler yükleniyor…</b></div>}
          {!error && !loading && rows.length === 0 && (
            q.trim() || progress ? (
              <div className="fp-blank"><span className="fp-blank-ico">{I.inbox}</span><b>{q ? `"${q}" için sipariş bulunamadı` : "Süzgece uyan sipariş yok"}</b>Model kodu, model adı ya da fatura no ile arayın.
                <button type="button" className="fp-btn" onClick={resetFilters}>Süzgeci sıfırla</button></div>
            ) : (
              <div className="fp-blank"><span className="fp-blank-ico">{I.inbox}</span><b>Sevk edilebilir sipariş yok</b>Bu firmanın açık siparişi yok ya da tümü sevk edilmiş.
                {doneCount > 0 && <button type="button" className="fp-btn" onClick={() => setShowDone(true)}>Tamamı sevk edilenleri göster · {doneCount}</button>}</div>
            )
          )}
          {rows.map((r) => {
            if (r.kind === "head") return <div key={r.key} className="fp-group">{r.icon}{r.label}</div>;
            const o = r.o; const i = idx++;
            const checked = draft.has(o.orderId);
            const due = dueState(o.deadline);
            return (
              <button type="button" key={r.key} role="option" aria-selected={checked} tabIndex={-1} id={`${uid}-${i}`}
                className={`fp-row ${cursor === i ? "cur" : ""} ${checked ? "sel" : ""} ${o.remaining <= EPS ? "off" : ""}`}
                data-cursor={i} onMouseEnter={() => setCursorKey(r.key)} onClick={() => toggle(o)}>
                <span className={`fp-mark ${o.remaining <= EPS ? "off" : ""}`} style={{ ["--fp-hue" as any]: firmHue(o.modelCode || o.orderId) }}>{orderMark(o)}</span>
                <span className="fp-text">
                  <b>
                    {o.modelCode || "Model yok"}
                    {o.invoiceNumber && <span className="fp-code">{o.invoiceNumber}</span>}
                    {due && <span className={`fp-badge op-due ${due}`}>{due === "past" ? "Termin geçti" : "Termin yakın"}</span>}
                  </b>
                  <small>{[o.modelName, fmtDate(o.acceptanceDate), o.orderStatusName].filter(Boolean).join(" · ") || "—"}</small>
                </span>
                <span className="fp-side op-side">
                  <b className={`op-rem ${o.remaining <= EPS ? "zero" : ""}`}>Kalan {fmtQty(o.remaining)} {o.unit}</b>
                  <small className="op-num">{fmtQty(o.shipped)} / {fmtQty(o.quantity)} sevk</small>
                </span>
                <span className={`fp-check ${checked ? "" : "empty"}`} aria-hidden="true">{I.check}</span>
              </button>
            );
          })}
          {rows.length > 0 && !!q && filtered.length !== orders.length && <div className="fp-count">{filtered.length} / {orders.length} sipariş</div>}
        </div>

        <footer className="fp-foot">
          <span className="fp-keys"><kbd>↑</kbd><kbd>↓</kbd> gezin · <kbd>Enter</kbd> işaretle · <kbd>Ctrl</kbd>+<kbd>Enter</kbd> uygula · <kbd>Esc</kbd> vazgeç</span>
          <span className="fp-actions">
            {draft.size > 0 && <button type="button" className="fp-btn danger" onClick={clearAll}>Temizle</button>}
            <button type="button" className="fp-btn" onClick={onClose}>Vazgeç</button>
            <button type="button" className="fp-btn primary" onClick={commit}>{liveCount ? `Uygula · ${liveCount}` : "Uygula"}</button>
          </span>
        </footer>
        <div className="fp-sr" role="status" aria-live="polite" aria-atomic="true">{live}</div>
      </div>
    </div>,
    document.body,
  );
};

export default OrderPickerModal;
